import { relations } from 'drizzle-orm'
import {
  boolean,
  date,
  decimal,
  index,
  integer,
  pgEnum,
  pgTable,
  text,
} from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getIdReference,
  getNullableIdReference,
  getTimestampWithTimezone,
} from '../common/utils'
import { users } from './user'

export const allowanceKindEnum = pgEnum('allowance_kind', [
  'none',
  'meal',
  'half_per_diem',
  'per_diem',
])

export const hourSheetVarianceReasonEnum = pgEnum(
  'hour_sheet_variance_reason',
  ['visit_overran', 'customer_late', 'drive_overran', 'other'],
)

// Stored as text[] (not a pgEnum) so new reasons don't need a DB migration.
// Source of truth for the values — @markku/types builds its zod enum from this.
// Scope: the automatic distance/allowance calc was unreliable and km needs a
// manual check. Long days are owned by the variance mechanism, not this.
export const HOUR_SHEET_REVIEW_REASONS = [
  'distance_lookup_failed',
  'visit_missing_coords',
  'no_office_configured',
] as const

export const hourSheets = pgTable(
  'hour_sheets',
  {
    ...getBaseFields(),
    userId: getIdReference(() => users.id),
    date: date().notNull(),
    startedTime: text().notNull(),
    endedTime: text(),
    lunchMinutes: integer().notNull().default(0),
    allowance: allowanceKindEnum().notNull().default('none'),
    personalCarKm: integer(),
    driveFromOfficeMin: integer(),
    driveToOfficeMin: integer(),
    farthestVisitKm: decimal({ precision: 6, scale: 2 }),
    varianceReasonCategory: hourSheetVarianceReasonEnum(),
    varianceReasonNotes: text(),
    // The worker flags this when the auto-calculated allowance looks wrong (e.g.
    // a visit's address was missing/incorrect). They explain the issue in
    // `notes`; the approver verifies and corrects the allowance manually.
    allowanceDisputed: boolean().notNull().default(false),
    // Approver-facing flags (e.g. distance lookup failed, >7.5 h day).
    reviewReasons: text()
      .array()
      .notNull()
      .default([])
      .$type<(typeof HOUR_SHEET_REVIEW_REASONS)[number][]>(),
    notes: text(),
    approvedAt: getTimestampWithTimezone(),
    approvedByUserId: getNullableIdReference(() => users.id),
  },
  (table) => [
    ...getBaseIndexes('hour_sheets', table),
    index('hour_sheets_user_date_idx').on(table.userId, table.date),
  ],
)

export const hourSheetsRelations = relations(hourSheets, ({ one }) => ({
  user: one(users, {
    fields: [hourSheets.userId],
    references: [users.id],
    relationName: 'hourSheetUser',
  }),
  approvedByUser: one(users, {
    fields: [hourSheets.approvedByUserId],
    references: [users.id],
    relationName: 'hourSheetApprover',
  }),
}))
